import { useEffect, useState } from "react";
import { toast } from "sonner";
import { UserPlus, Trash2, Ban, RefreshCw, LogOut, Mail } from "lucide-react";
import { api, apiErr, inputCls, labelCls } from "./adminShared";

export default function AdminStaff() {
  const [data, setData] = useState({ staff: [], invites: [] });
  const [form, setForm] = useState({ name: "", email: "" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/staff").then((r) => setData(r.data)).catch((e) => toast.error(apiErr(e)));
  useEffect(() => { load(); }, []);

  const invite = async () => {
    if (!form.name || !form.email) { toast.error("Enter a name and email"); return; }
    setBusy(true);
    try { await api.post("/admin/staff/invite", form); toast.success("Invitation sent"); setForm({ name: "", email: "" }); load(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const act = async (fn, msg) => { try { await fn(); toast.success(msg); load(); } catch (e) { toast.error(apiErr(e)); } };

  return (
    <div className="space-y-6" data-testid="admin-staff">
      <section className="bg-white rounded-2xl border border-[#E6DEC3] p-5">
        <h3 className="font-serif text-xl text-[#2C2621] mb-3">Invite a staff member</h3>
        <div className="grid sm:grid-cols-3 gap-2">
          <div><label className={labelCls}>Name</label><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} data-testid="invite-name" /></div>
          <div><label className={labelCls}>Email</label><input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} data-testid="invite-email" /></div>
          <div className="flex items-end"><button onClick={invite} disabled={busy} className="w-full inline-flex items-center justify-center gap-1 bg-[#B87333] text-white rounded-full py-2.5 text-sm font-medium disabled:opacity-60" data-testid="invite-send"><UserPlus size={15} /> Send invite</button></div>
        </div>
        <p className="text-xs text-[#635B52] mt-2">Single-use link, expires in 24 hours. Staff set their own password. No public staff registration.</p>
      </section>

      <section>
        <p className={labelCls}>Pending invitations</p>
        <div className="mt-2 space-y-2" data-testid="invite-list">
          {data.invites.map((i) => (
            <div key={i.invite_id} className="flex items-center justify-between bg-white border border-[#E6DEC3] rounded-xl p-3 text-sm">
              <span className="text-[#2C2621]"><Mail size={14} className="inline mr-1 text-[#B87333]" />{i.name} · {i.email}</span>
              <button onClick={() => act(() => api.post(`/admin/staff/invites/${i.invite_id}/revoke`), "Invitation revoked")} className="text-red-600 text-xs" data-testid={`revoke-${i.invite_id}`}>Revoke</button>
            </div>
          ))}
          {!data.invites.length && <p className="text-sm text-[#635B52]">No pending invitations.</p>}
        </div>
      </section>

      <section>
        <p className={labelCls}>Staff members</p>
        <div className="mt-2 space-y-2" data-testid="staff-list">
          {data.staff.map((s) => (
            <div key={s.id || s.email} className="flex flex-wrap items-center justify-between gap-2 bg-white border border-[#E6DEC3] rounded-xl p-3 text-sm">
              <span className="text-[#2C2621]">{s.name} · {s.email} {s.suspended && <span className="text-red-600">(suspended)</span>}
                {s.permissions?.marketing_draft && <span className="ml-1 text-xs bg-[#B87333]/15 text-[#B87333] rounded-full px-2 py-0.5">Marketing draft</span>}</span>
              <div className="flex gap-1 flex-wrap">
                <button onClick={() => act(() => api.post(`/admin/staff/${s.id}/permissions`, { marketing_draft: !s.permissions?.marketing_draft }), "Marketing access updated")} className="inline-flex items-center gap-1 border border-[#E6DEC3] rounded-full px-3 py-1.5 text-xs" data-testid={`marketing-toggle-${s.id}`}>{s.permissions?.marketing_draft ? "Revoke marketing" : "Grant marketing draft"}</button>
                {s.suspended
                  ? <button onClick={() => act(() => api.post(`/admin/staff/${s.id}/reactivate`), "Reactivated")} className="inline-flex items-center gap-1 border border-[#E6DEC3] rounded-full px-3 py-1.5 text-xs" data-testid={`reactivate-${s.id}`}><RefreshCw size={12} /> Reactivate</button>
                  : <button onClick={() => act(() => api.post(`/admin/staff/${s.id}/suspend`), "Suspended & logged out")} className="inline-flex items-center gap-1 border border-amber-300 text-amber-700 rounded-full px-3 py-1.5 text-xs" data-testid={`suspend-${s.id}`}><Ban size={12} /> Suspend</button>}
                <button onClick={() => act(() => api.post(`/admin/staff/${s.id}/logout-all`), "Logged out of all devices")} className="inline-flex items-center gap-1 border border-[#E6DEC3] rounded-full px-3 py-1.5 text-xs" data-testid={`logoutall-${s.id}`}><LogOut size={12} /> Log out all</button>
                <button onClick={() => { if (window.confirm("Remove this staff member and revoke access?")) act(() => api.delete(`/admin/staff/${s.id}`), "Removed"); }} className="inline-flex items-center gap-1 border border-red-300 text-red-700 rounded-full px-3 py-1.5 text-xs" data-testid={`remove-${s.id}`}><Trash2 size={12} /> Remove</button>
              </div>
            </div>
          ))}
          {!data.staff.length && <p className="text-sm text-[#635B52]">No staff yet.</p>}
        </div>
      </section>
    </div>
  );
}
